import { ColumnsType } from "antd/es/table";
import { Space, Dropdown, Tag, Tooltip, Typography } from "antd";
import ColumnSearch from "./ColumnSearch";
import ColumnDateFilter from "./ColumnDateFilter";
import { BotoesAcoes } from "../styles";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  getMenuItems,
  getMenuItemsProcura,
} from "../constants/itemsActionColumns";
import { formatDate } from "../../../uteis/dateUtils";
import { formatValorReal } from "../../../uteis/formatValorReal";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarCircleOutlined,
  EditOutlined,
  FrownFilled,
  SearchOutlined,
  SmileFilled,
  SyncOutlined,
  WarningOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

type TipoInteresse =
  | "ALUGAR"
  | "VENDER"
  | "TROCAR"
  | "COMPRAR"
  | "NAO_DEFINIDO"
  | "ALUGAR_TROCAR";

const tipoInteresseColors: Record<TipoInteresse, string> = {
  ALUGAR: "blue",
  VENDER: "green",
  TROCAR: "orange",
  COMPRAR: "yellow",
  ALUGAR_TROCAR: "purple",
  NAO_DEFINIDO: "default",
};

const statusMap = new Map<string, JSX.Element>([
  ["ATIVO", <CheckCircleOutlined style={{ color: "green", fontSize: 24 }} />],
  ["INATIVO", <CloseCircleOutlined style={{ color: "red", fontSize: 24 }} />],
  ["NEGOCIANDO", <SyncOutlined style={{ color: "#1677ff", fontSize: 24 }} />],
  [
    "NEGOCIANDO_ALTERADO",
    <EditOutlined style={{ color: "#faad14", fontSize: 24 }} />,
  ],
  [
    "FINALIZADO_SUCESSO",
    <SmileFilled style={{ color: "green", fontSize: 24 }} />,
  ],
  [
    "FINALIZADO_FRACASSO",
    <FrownFilled style={{ color: "red", fontSize: 24 }} />,
  ],
  [
    "SEM_INTERESSE_PERIODO_PROCURADO",
    <SearchOutlined style={{ color: "#fa8c16", fontSize: 24 }} />,
  ],
  [
    "SEM_INTERESSE_PERIODO_ENCONTRADO",
    <CalendarOutlined style={{ color: "#fa541c", fontSize: 24 }} />,
  ],
  [
    "DESATUALIZADO",
    <WarningOutlined style={{ color: "#fa541c", fontSize: 24 }} />,
  ],
]);

const tipoInteresseFilters = [
  { text: "Alugar", value: "ALUGAR" },
  { text: "Trocar", value: "TROCAR" },
  { text: "Alugar ou trocar", value: "ALUGAR_TROCAR" },
];

const origemProcuraFilters = [
  { text: "POST", value: "POST" },
  { text: "INT", value: "INT" },
];

const origemDispFilters = [
  { text: "PEU", value: "PEU" },
  { text: "POST", value: "POST" },
];

const statusFilters = [
  { text: "Ativo", value: "ATIVO" },
  { text: "Inativo", value: "INATIVO" },
  { text: "Negociando", value: "NEGOCIANDO" },
  { text: "Negociando alterado", value: "NEGOCIANDO_ALTERADO" },
  { text: "Finalizado sucesso", value: "FINALIZADO_SUCESSO" },
  { text: "Finalizado fracasso", value: "FINALIZADO_FRACASSO" },
  {
    text: "Sem interesse no periodo procurado",
    value: "SEM_INTERESSE_PERIODO_PROCURADO",
  },
  {
    text: "Sem interesse no periodo encontrado",
    value: "SEM_INTERESSE_PERIODO_ENCONTRADO",
  },
  { text: "Desatualizado", value: "DESATUALIZADO" },
];

const nivelFilters = Array.from({ length: 9 }, (_, index) => ({
  text: `Nível ${index + 1}`,
  value: index + 1,
}));

const getTipoInteresseColor = (tipo: string): string => {
  return tipoInteresseColors[tipo as TipoInteresse] || "default";
};

const isSemInteresseTipo = (text: string) =>
  String(text || "").includes("SEM_INTERESSE");

const getTipoInteresseDisponivelColor = (tipo: string): string => {
  if (isSemInteresseTipo(tipo)) {
    return "red";
  }

  return getTipoInteresseColor(tipo);
};

const formatTipoLabel = (text: string) => {
  if (!text) return "";

  if (isSemInteresseTipo(text)) {
    return text
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (letra) => letra.toUpperCase());
  }

  return text[0] + text.slice(1).toLowerCase();
};

export const ColumnsProcura = (
  onClick: (e: any) => void,
  setCompatibilidadeProcuraId: (e: any) => void,
  setCompatibilidadeOrigemProcura: (e: any) => void
): ColumnsType<any> => {
  const items = getMenuItemsProcura();
  const usuarioProcuraSearch = ColumnSearch<any>({ dataIndex: "usuarioProcura" });
  const nomeEmpreendimentoSearch = ColumnSearch<any>({
    dataIndex: "nomeEmpreendimento",
  });
  const dataIniProcuraFilter = ColumnDateFilter<any>({
    dataIndex: "dataIniProcura",
  });
  const dataFimProcuraFilter = ColumnDateFilter<any>({
    dataIndex: "dataFimProcura",
  });

  return [
    {
      title: "Origem Procura",
      dataIndex: "origemProcura",
      key: "origemProcura",
      width: "10%",
      filters: origemProcuraFilters,
      onFilter: (value, record) => record.origemProcura === value,
      render: (text) => (
        <Text strong type={text === "PEU" ? "success" : "warning"}>
          {text}
        </Text>
      ),
    },
    {
      title: "Usuário",
      dataIndex: "usuarioProcura",
      key: "usuarioProcura",
      width: "20%",
      ...usuarioProcuraSearch,
    },
    {
      title: "Empreendimento",
      dataIndex: "nomeEmpreendimento",
      key: "nomeEmpreendimento",
      width: "20%",
      ...nomeEmpreendimentoSearch,
    },
    {
      title: "Tipo Interesse Procura",
      dataIndex: "tipoInteresseProcura",
      key: "tipoInteresseProcura",
      width: "12%",
      filters: tipoInteresseFilters,
      onFilter: (value, record) => record.tipoInteresseProcura === value,
      render: (text: string) => (
        <Tag color={getTipoInteresseColor(text)}>{formatTipoLabel(text)}</Tag>
      ),
    },
    {
      title: "Valor Procura",
      dataIndex: "valorProcura",
      key: "valorProcura",
      width: "12%",
      render: (text) => (
        <span>
          <DollarCircleOutlined style={{ marginRight: 4, color: "#52c41a" }} />
          {formatValorReal(text)}
        </span>
      ),
    },
    {
      title: "Data Início Procura",
      dataIndex: "dataIniProcura",
      key: "dataIniProcura",
      width: "10%",
      ...dataIniProcuraFilter,
      render: (text) => (
        <span>
          <CalendarOutlined style={{ marginRight: 4, color: "#8c8c8c" }} />
          {formatDate(text)}
        </span>
      ),
    },
    {
      title: "Data Fim Procura",
      dataIndex: "dataFimProcura",
      key: "dataFimProcura",
      width: "10%",
      ...dataFimProcuraFilter,
      render: (text) => (
        <span>
          <CalendarOutlined style={{ marginRight: 4, color: "#8c8c8c" }} />
          {formatDate(text)}
        </span>
      ),
    },
    {
      title: "Ações",
      dataIndex: "operation",
      key: "operation",
      width: "8%",
      align: "center",
      render: (_, row) => (
        <Dropdown menu={{ items, onClick }} trigger={["click"]}>
          <BotoesAcoes
            onClick={() => {
              setCompatibilidadeProcuraId(row.procuraId);
              setCompatibilidadeOrigemProcura(row.origemProcura);
            }}
          >
            <Space>
              <SettingsIcon />
            </Space>
          </BotoesAcoes>
        </Dropdown>
      ),
    },
  ];
};

export const ColumnsInteresse = (
  onClick: (e: any) => void,
  setCompatibilidadeId: (e: any) => void,
  setCompatibilidadeMaisInfoId: (e: any) => void,
  setCompatibilidadeOrigem: (e: any) => void,
  setCompatibilidadeObservacao: (e: any) => void,
  setCompatibilidadeStatus: (e: any) => void,
  setCompatibilidadeValorDisp: (e: any) => void
): ColumnsType<any> => {
  const items = getMenuItems();
  const usuarioDispSearch = ColumnSearch<any>({ dataIndex: "usuarioDisp" });
  const dataIniDispFilter = ColumnDateFilter<any>({ dataIndex: "dataIniDisp" });
  const dataFimDispFilter = ColumnDateFilter<any>({ dataIndex: "dataFimDisp" });

  const getNivelColor = (nivel: number): string => {
    switch (nivel) {
      case 1:
        return "green";
      case 2:
        return "cyan";
      case 3:
        return "blue";
      case 4:
        return "geekblue";
      case 5:
        return "purple";
      case 6:
        return "magenta";
      case 7:
        return "orange";
      case 8:
        return "volcano";
      case 9:
        return "red";
      default:
        return "default";
    }
  };

  return [
    {
      title: "Origem Dispo.",
      dataIndex: "origemDisp",
      key: "origemDisp",
      width: "12%",
      filters: origemDispFilters,
      onFilter: (value, record) => record.origemDisp === value,
      render: (text) => (
        <Text strong type={text === "PEU" ? "success" : "warning"}>
          {text}
        </Text>
      ),
    },
    {
      title: "Usuário",
      dataIndex: "usuarioDisp",
      key: "usuarioDisp",
      width: "20%",
      ...usuarioDispSearch,
    },
    {
      title: "Tipo Interesse Disponível",
      dataIndex: "tipoInteresseDisponivel",
      key: "tipoInteresseDisponivel",
      width: "18%",
      filters: tipoInteresseFilters,
      onFilter: (value, record) => record.tipoInteresseDisponivel === value,
      render: (text: string) => (
        <Tag color={getTipoInteresseDisponivelColor(text)}>
          {formatTipoLabel(text)}
        </Tag>
      ),
    },
    {
      title: "Valor Disponível",
      dataIndex: "valorDisp",
      key: "valorDisp",
      width: "12%",
      render: (text) => (
        <span>
          <DollarCircleOutlined style={{ marginRight: 4, color: "#52c41a" }} />
          {formatValorReal(text)}
        </span>
      ),
    },
    {
      title: "Data Início Disponível",
      dataIndex: "dataIniDisp",
      key: "dataIniDisp",
      width: "10%",
      ...dataIniDispFilter,
      render: (text) => (
        <span>
          <CalendarOutlined style={{ marginRight: 4, color: "#8c8c8c" }} />
          {formatDate(text)}
        </span>
      ),
    },
    {
      title: "Data Fim Disponível",
      dataIndex: "dataFimDisp",
      key: "dataFimDisp",
      width: "10%",
      ...dataFimDispFilter,
      render: (text) => (
        <span>
          <CalendarOutlined style={{ marginRight: 4, color: "#8c8c8c" }} />
          {formatDate(text)}
        </span>
      ),
    },
    {
      title: "Nível",
      dataIndex: "nivel",
      key: "nivel",
      width: "8%",
      align: "center",
      filters: nivelFilters,
      onFilter: (value, record) => record.nivel === value,
      render: (_, row) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <Tooltip title={row.descricao}>
            <Tag color={getNivelColor(row.nivel)}>Nível {row.nivel}</Tag>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "8%",
      align: "center",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (_, row) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {statusMap.has(row.status) && (
            <Tooltip
              title={row.status
                .replace(/_/g, " ")
                .toLowerCase()
                .replace(/\b\w/g, (l: any) => l.toUpperCase())}
            >
              {statusMap.get(row.status)}
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Ações",
      dataIndex: "operation",
      key: "operation",
      width: "8%",
      align: "center",
      render: (_, row) => (
        <Dropdown menu={{ items, onClick }} trigger={["click"]}>
          <BotoesAcoes
            onClick={() => {
              setCompatibilidadeId(row.id);
              setCompatibilidadeMaisInfoId(row.dispId ?? row.id);
              setCompatibilidadeOrigem(row.origemDisp);
              setCompatibilidadeObservacao(row.observacao);
              setCompatibilidadeStatus(row.status);
              setCompatibilidadeValorDisp(row.valorDisp);
            }}
          >
            <Space>
              <SettingsIcon />
            </Space>
          </BotoesAcoes>
        </Dropdown>
      ),
    },
  ];
};
